/**
 * API Service for AMP Prediction Market
 * Handles all backend API calls
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build auth headers from localStorage JWT (used by email/password users
// who don't have a server-side session cookie in cross-origin contexts).
const authHeaders = (extra = {}) => {
  const token = (typeof window !== 'undefined') ? localStorage.getItem('session_token') : null;
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
};

class ApiService {
  // Auth endpoints
  async exchangeSession(sessionId) {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) throw new Error('Session exchange failed');
    return response.json();
  }

  async getCurrentUser() {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error('Not authenticated');
    return response.json();
  }

  async logout() {
    const response = await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    return response.json();
  }

  // Subscription endpoints
  async createCheckoutSession(tier = 'monthly') {
    const originUrl = window.location.origin;
    const response = await fetch(`${API_URL}/api/subscription/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ origin_url: originUrl, tier: tier })
    });
    if (!response.ok) throw new Error('Failed to create checkout');
    return response.json();
  }

  async getSubscriptionTiers() {
    const response = await fetch(`${API_URL}/api/subscription/tiers`);
    if (!response.ok) throw new Error('Failed to get subscription tiers');
    return response.json();
  }

  async checkPaymentStatus(sessionId) {
    const response = await fetch(`${API_URL}/api/subscription/status/${sessionId}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  }

  // Wallet endpoints
  async getWallet() {
    const response = await fetch(`${API_URL}/api/wallet`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get wallet');
    return response.json();
  }

  async claimFaucet() {
    const response = await fetch(`${API_URL}/api/wallet/faucet`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to claim faucet');
    }
    return response.json();
  }

  async getLedger(limit = 50, cursor = null) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);
    
    const response = await fetch(`${API_URL}/api/wallet/ledger?${params}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get ledger');
    return response.json();
  }

  // Markets endpoints
  async getMarkets(status = null, category = null, search = null, limit = 20, cursor = null, sort = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (sort) params.append('sort', sort);
    params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);
    
    const response = await fetch(`${API_URL}/api/markets?${params}`);
    if (!response.ok) throw new Error('Failed to get markets');
    return response.json();
  }

  async getMarket(marketId) {
    const response = await fetch(`${API_URL}/api/markets/${marketId}`);
    if (!response.ok) throw new Error('Market not found');
    return response.json();
  }

  async getMarketPrices(marketId) {
    const response = await fetch(`${API_URL}/api/markets/${marketId}/prices`);
    if (!response.ok) throw new Error('Failed to get prices');
    return response.json();
  }

  // Trading endpoints (requires subscription)
  async executeTrade(marketId, side, outcome, spendAmount = null, shares = null) {
    const body = { side, outcome };
    if (spendAmount !== null) body.spend_amount = spendAmount;
    if (shares !== null) body.shares = shares;
    
    const response = await fetch(`${API_URL}/api/markets/${marketId}/trade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Trade failed');
    }
    return response.json();
  }

  // Positions endpoints
  async getPositions(marketId = null) {
    const params = new URLSearchParams();
    if (marketId) params.append('market_id', marketId);
    
    const response = await fetch(`${API_URL}/api/positions?${params}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get positions');
    return response.json();
  }

  // Ideas endpoints
  async getIdeas(category = null, status = null, sort = 'votes', limit = 20) {
    const params = new URLSearchParams({ sort, limit: limit.toString() });
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    
    const response = await fetch(`${API_URL}/api/ideas/?${params}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get ideas');
    return response.json();
  }

  async createIdea(title, description, category, subcategory, outcomeType = 'binary') {
    const response = await fetch(`${API_URL}/api/ideas/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title, description, category, subcategory, outcome_type: outcomeType })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create idea');
    }
    return response.json();
  }

  async voteIdea(ideaId, vote) {
    const response = await fetch(`${API_URL}/api/ideas/${ideaId}/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ vote })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to vote');
    }
    return response.json();
  }

  // Watchlist endpoints
  async getWatchlist() {
    const response = await fetch(`${API_URL}/api/watchlist/`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get watchlist');
    return response.json();
  }

  async addToWatchlist(marketId) {
    const response = await fetch(`${API_URL}/api/watchlist/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ market_id: marketId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add to watchlist');
    }
    return response.json();
  }

  async removeFromWatchlist(marketId) {
    const response = await fetch(`${API_URL}/api/watchlist/${marketId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    return response.json();
  }

  async checkWatchlist(marketId) {
    const response = await fetch(`${API_URL}/api/watchlist/check/${marketId}`, {
      credentials: 'include'
    });
    return response.json();
  }

  // User Profile endpoints
  async getProfile() {
    const response = await fetch(`${API_URL}/api/user/profile`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error('Failed to get profile');
    return response.json();
  }

  async setUsername(username) {
    const response = await fetch(`${API_URL}/api/user/username`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ username })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to set username');
    }
    return response.json();
  }

  async getMyIdeas() {
    const response = await fetch(`${API_URL}/api/user/my-ideas`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get ideas');
    return response.json();
  }

  // Admin endpoints
  async adminGetIdeas(adminKey, status = null) {
    const params = new URLSearchParams({ limit: '100' });
    if (status) params.append('status', status);
    
    const response = await fetch(`${API_URL}/api/admin-panel/ideas?${params}`, {
      headers: { 'X-Admin-Key': adminKey },
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 403) throw new Error('403: Admin access required');
      throw new Error('Failed to get ideas');
    }
    return response.json();
  }

  async adminGetStats(adminKey) {
    const response = await fetch(`${API_URL}/api/admin-panel/stats`, {
      headers: { 'X-Admin-Key': adminKey },
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get stats');
    return response.json();
  }

  async adminUpdateIdeaStatus(adminKey, ideaId, status, adminNotes = null) {
    const body = { status };
    if (adminNotes) body.admin_notes = adminNotes;
    
    const response = await fetch(`${API_URL}/api/admin-panel/ideas/${ideaId}/status`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'X-Admin-Key': adminKey 
      },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Failed to update idea status');
    return response.json();
  }

  async adminLoadToMarket(adminKey, ideaId, closesAt, initialYesPrice = 50) {
    const response = await fetch(`${API_URL}/api/admin-panel/ideas/${ideaId}/load-to-market`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Admin-Key': adminKey 
      },
      credentials: 'include',
      body: JSON.stringify({ 
        idea_id: ideaId, 
        closes_at: closesAt,
        initial_yes_price: initialYesPrice
      })
    });
    if (!response.ok) throw new Error('Failed to load to market');
    return response.json();
  }

  // Dashboard endpoints
  async getDashboardSummary() {
    const response = await fetch(`${API_URL}/api/dashboard/summary`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get dashboard summary');
    return response.json();
  }

  async getWallet() {
    const response = await fetch(`${API_URL}/api/dashboard/wallet`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get wallet');
    return response.json();
  }

  async getPositions() {
    const response = await fetch(`${API_URL}/api/dashboard/positions`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get positions');
    return response.json();
  }

  async getTrades(limit = 20, offset = 0) {
    const response = await fetch(`${API_URL}/api/dashboard/trades?limit=${limit}&offset=${offset}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get trades');
    return response.json();
  }

  async getRecentActivity(limit = 10) {
    const response = await fetch(`${API_URL}/api/dashboard/activity?limit=${limit}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get activity');
    return response.json();
  }

  // Search endpoints
  async globalSearch(query, category = null, limit = 20) {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    if (category) params.append('category', category);
    
    const response = await fetch(`${API_URL}/api/search?${params}`);
    if (!response.ok) throw new Error('Search failed');
    return response.json();
  }

  async getCategories() {
    const response = await fetch(`${API_URL}/api/categories`);
    if (!response.ok) throw new Error('Failed to get categories');
    return response.json();
  }

  async getLiveSports() {
    const response = await fetch(`${API_URL}/api/sports/live`);
    if (!response.ok) throw new Error('Failed to get live sports');
    return response.json();
  }

  async getGeneratedPredictions(category = null, sport = null, count = 10) {
    const params = new URLSearchParams({ count: count.toString() });
    if (category) params.append('category', category);
    if (sport) params.append('sport', sport);
    
    const response = await fetch(`${API_URL}/api/predictions/generate?${params}`);
    if (!response.ok) throw new Error('Failed to get predictions');
    return response.json();
  }

  // Admin user statistics
  async adminExportUserStats(adminKey, format = 'csv') {
    const response = await fetch(`${API_URL}/api/admin-panel/export/user-statistics?format=${format}`, {
      headers: { 'X-Admin-Key': adminKey }
    });
    if (!response.ok) throw new Error('Failed to export user statistics');
    return response.blob();
  }

  async adminGetAnalytics(adminKey) {
    const response = await fetch(`${API_URL}/api/admin-panel/analytics/overview`, {
      headers: { 'X-Admin-Key': adminKey },
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to get analytics');
    return response.json();
  }

  // Scraper endpoints
  async getScrapedPredictions(source = null, category = null, limit = 20) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (source) params.append('source', source);
    if (category) params.append('category', category);
    
    const response = await fetch(`${API_URL}/api/scraper/results?${params}`);
    if (!response.ok) throw new Error('Failed to get scraped predictions');
    return response.json();
  }

  async triggerScrape() {
    const response = await fetch(`${API_URL}/api/scraper/run`);
    if (!response.ok) throw new Error('Failed to trigger scrape');
    return response.json();
  }

  async getScraperSources() {
    const response = await fetch(`${API_URL}/api/scraper/sources`);
    if (!response.ok) throw new Error('Failed to get scraper sources');
    return response.json();
  }

  // Sports Scores endpoints
  async getSportsScores(sport, date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    
    const response = await fetch(`${API_URL}/api/scores/${sport}?${params}`);
    if (!response.ok) throw new Error('Failed to get sports scores');
    return response.json();
  }

  async getNBAScores(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    
    const response = await fetch(`${API_URL}/api/scores/nba/balldontlie?${params}`);
    if (!response.ok) throw new Error('Failed to get NBA scores');
    return response.json();
  }

  async getSoccerScores(competition = 'PL', date = null) {
    const params = new URLSearchParams({ competition });
    if (date) params.append('date', date);
    
    const response = await fetch(`${API_URL}/api/scores/soccer/football-data?${params}`);
    if (!response.ok) throw new Error('Failed to get soccer scores');
    return response.json();
  }
}

export const apiService = new ApiService();
export default apiService;
